import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import { Avatar, Button, CircularProgress } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useUser } from "../../common/contexts/userContext";
import { uploadAvatar } from "../../services/userService";
import "./MobileProfile.scss";

interface UploadPhotoProps {
  buttonLabel: string;
  isDesktop?: boolean;
}

const UploadPhoto: React.FC<UploadPhotoProps> = ({
  buttonLabel,
  isDesktop,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  const { user, setUser } = useUser();

  const [imgBlobLink, setImgBlobLink] = useState<string>(user.avatarUrl);
  const [uploading, setUploading] = useState(false);

  const hasPhoto = imgBlobLink !== "";
  const size = isDesktop ? 200 : 125;

  const openPicker = () => {
    if (!uploading) ref.current?.click();
  };

  const withCacheBust = (url: string) => {
    if (!url) return "";
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}v=${Date.now()}`;
  };

  const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedImage = e.target.files?.[0];
    // Reset the input so picking the same file again still fires onChange.
    e.target.value = "";
    if (!uploadedImage || !validateFileType(uploadedImage.name)) return;

    try {
      setUploading(true);
      const data = await uploadAvatar(user.id, uploadedImage);
      setImgBlobLink(withCacheBust(data.avatarUrl));
    } catch {
      alert("Couldn't update your photo — please try again.");
    } finally {
      setUploading(false);
    }
  };

  const validateFileType = (fileName: string) => {
    var extFile = getExt(fileName);
    if (extFile === "jpg" || extFile === "jpeg" || extFile === "png") {
      return true;
    } else {
      alert("Only jpg/jpeg and png files are allowed!");
      return false;
    }
  };

  const getExt = (fileName: string) => {
    var idxDot = fileName.lastIndexOf(".") + 1;
    return fileName.substr(idxDot, fileName.length).toLowerCase();
  };

  useEffect(() => {
    setUser((prev) => ({ ...prev, avatarUrl: imgBlobLink }));
  }, [imgBlobLink, setUser]);

  return (
    <div className="uploadPhoto__root">
      <button
        type="button"
        className="uploadPhoto__avatarBtn"
        onClick={openPicker}
        disabled={uploading}
        aria-label={hasPhoto ? "Change profile photo" : "Upload profile photo"}
        style={{ marginTop: isDesktop ? 10 : undefined }}
      >
        <Avatar
          src={imgBlobLink}
          sx={{
            height: size,
            width: size,
            border: "#FAFAFA solid 2px",
          }}
          alt="User Avatar"
        />
        <span className="uploadPhoto__badge" aria-hidden="true">
          {uploading ? (
            <CircularProgress size={18} thickness={5} color="inherit" />
          ) : (
            <PhotoCameraRoundedIcon sx={{ fontSize: 18 }} />
          )}
        </span>
      </button>

      <UploadButton
        buttonLabel={hasPhoto ? "Change profile photo" : buttonLabel}
        onClick={openPicker}
        disabled={uploading}
      />

      <input
        id="filePicker"
        className="uploadPhoto__input"
        type="file"
        onChange={uploadFile}
        accept="image/*"
        ref={ref}
      />
    </div>
  );
};

export default UploadPhoto;

export const UploadButton: React.FC<UploadButtonProps> = ({
  onClick,
  buttonLabel,
  disabled,
}) => {
  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={<CloudUploadIcon />}
      onClick={() => onClick()}
      disabled={disabled}
    >
      {buttonLabel}
    </Button>
  );
};

interface UploadButtonProps {
  onClick: () => void;
  buttonLabel: string;
  disabled?: boolean;
}
